import React, { useEffect, useState } from 'react';
import { Table, Typography, Input, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getRegByEstReport } from '@app/api/reports.api';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

interface ReportHeader {
  key: string;
  label: string;
  type: 'number' | 'string' | 'boolean';
}

interface ReportRow {
  [key: string]: any;
}

interface ApiResponse {
  report: {
    headers: ReportHeader[];
    rows: ReportRow[];
  };
}

interface PREstablishmentReportTableProps {
  filters: any;
  onRowClick?: (record: ReportRow) => void;
  reportKey: string;
}

const SearchFilter: React.FC<{ onSearch: (keyword: string) => void }> = ({ onSearch }) => (
  <div className="hide-in-pdf" style={{ marginBottom: 16 }}>
    <Input.Search
      placeholder="Search establishments, customers, products..."
      allowClear
      onChange={(e) => onSearch(e.target.value)}
      onSearch={onSearch}
      enterButton
      style={{ width: 300 }}
    />
  </div>
);

export const PREstablishmentReportTable: React.FC<PREstablishmentReportTableProps> = ({ filters, onRowClick }) => {
  const [headers, setHeaders] = useState<ReportHeader[]>([]);
  const [rawData, setRawData] = useState<ReportRow[]>([]);
  const [filteredData, setFilteredData] = useState<ReportRow[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!filters) return;

      const storageKey = `report_data`;
      const cachedData = sessionStorage.getItem(storageKey);

      let useCache = false;
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData) as { filters: any; response: ApiResponse };

          // Only use cache if filters match exactly
          useCache =
            parsed.filters.reportKey === filters.reportKey &&
            parsed.filters.pr_category_id === filters.pr_category_id &&
            parsed.filters.model === filters.model;

          if (useCache && parsed.response?.report) {
            setHeaders(parsed.response.report.headers || []);
            setRawData(parsed.response.report.rows || []);
            setFilteredData(parsed.response.report.rows || []);
            return;
          }
        } catch (err) {
          console.warn('Failed to parse cached report data, refetching...');
          sessionStorage.removeItem(storageKey);
        }
      }

      try {
        const response: ApiResponse = await getRegByEstReport(filters);

        const reportHeaders = response.report?.headers || [];
        const reportData = response.report?.rows || [];

        setHeaders(reportHeaders);
        setRawData(reportData);
        setFilteredData(reportData);

        // Save both filters and response to sessionStorage
        sessionStorage.setItem(storageKey, JSON.stringify({ filters, response }));
      } catch (err) {
        message.error('Failed to fetch report data.');
        console.error(err);
        setHeaders([]);
        setRawData([]);
        setFilteredData([]);
      }
    };

    fetchData();
  }, [filters]);

  const handleSearch = (keyword: string) => {
    if (!keyword || keyword.trim() === '') {
      setFilteredData(rawData);
      return;
    }

    const lower = keyword.toLowerCase();
    const results = rawData.filter((row) => {
      // Search through all string fields
      return Object.entries(row).some(([key, value]) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(lower);
        }
        return false;
      });
    });

    setFilteredData(results);
  };

  const columns: ColumnsType<ReportRow> = headers.map((header) => {
    const column: any = {
      title: header.label,
      dataIndex: header.key,
      key: header.key,
    };

    if (header.type === 'boolean') {
      column.render = (value: boolean) => (value ? '✓' : '');
      column.align = 'center';
    } else if (header.key === 'id') {
      column.render = (value: any, record: ReportRow) => value || record.location_id || '-';
    }
    return column;
  });

  return (
    <div>
      <Title level={4}>Product Registrations by Establishments/Locations</Title>
      <SearchFilter onSearch={handleSearch} />
      <Table
        size="small"
        columns={columns}
        dataSource={filteredData}
        rowKey={(record) => record.location_id || record.id || Math.random()}
        pagination={false}
        onRow={(record) => ({
          onClick: () => {
            if (onRowClick) onRowClick(record);
          },
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};
